import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Package, Plus, Tags } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ProductCard } from '../../components/nursery';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import type { NurseryProduct } from '../../types/nursery';
import { groupByCrop, NO_CROP, productCrop as cropOf } from '../../utils/cropGroups';

/** Within a crop: by name, the same name's tree types together. */
const byName = (a: NurseryProduct, b: NurseryProduct) =>
  a.name.localeCompare(b.name) || (a.tree_type_code ?? '').localeCompare(b.tree_type_code ?? '');

/**
 * Umbrella products: one name and price a customer buys ("Dwarf Eureka
 * Lemon", "Pink Lady Apple") over the stock items it is filled from. Stock,
 * lots and picking keep seeing the real stock items.
 */
function ProductList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'ProductList' });
  const navigate = useNavigate();
  const { hasAccess: canAdmin } = useModuleLicense('nursery', 'admin');
  const [products, setProducts] = useState<NurseryProduct[] | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [crop, setCrop] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    nurseryService.getProducts()
      .then((res) => {
        if (cancelled) return;
        if (res.success) setProducts(res.data || []);
        else { setProducts([]); setError(errorMessage(res, t('loadFailed'))); }
      })
      .catch((e) => { if (!cancelled) { setProducts([]); setError(errorMessage(e, t('loadFailed'))); } });
    return () => { cancelled = true; };
  }, [t]);

  const shown = useMemo(() => (products ?? []).filter((p) => showRetired || p.active), [products, showRetired]);
  const retiredCount = (products ?? []).filter((p) => !p.active).length;

  const groups = useMemo(() => groupByCrop(shown, cropOf, byName), [shown]);
  // A crop whose only products are retired goes when they are hidden.
  const cropShown = groups.some((g) => g.crop === crop) ? crop : 'all';
  const shownGroups = cropShown === 'all' ? groups : groups.filter((g) => g.crop === cropShown);
  // A server from before the crop was sent names none: no headings, rather than one saying "No crop type".
  const hasCrops = groups.some((g) => g.crop !== NO_CROP);
  const cropName = (c: string) => (c === NO_CROP ? t('noCrop') : c);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Tags className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            {/* The stock items a product is filled from are set up there. */}
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/stock-items')}>
              <Package className="h-4 w-4" />
              {t('stockItems')}
            </Button>
            {canAdmin && (
              <Button variant="primary" size="sm" onClick={() => navigate('/nursery/products/new')}>
                <Plus className="h-4 w-4" />
                {t('newProduct')}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-sm text-neutral-600 mb-4 max-w-3xl">
          <Trans t={t} i18nKey="intro" components={{ b: <span className="font-medium" /> }} />
        </p>

        {(retiredCount > 0 || (hasCrops && groups.length > 1)) && (
          <div className="flex gap-x-3 gap-y-2 flex-wrap items-center justify-between mb-4">
            {retiredCount > 0 ? (
              <label className="inline-flex items-center gap-2 text-sm text-neutral-600">
                <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
                {t('showRetired', { count: retiredCount })}
              </label>
            ) : <span />}
            {hasCrops && groups.length > 1 && (
              <select
                aria-label={t('cropType')}
                value={cropShown}
                onChange={(e) => setCrop(e.target.value)}
                className="w-full sm:w-auto border border-neutral-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">{t('allCrops')}</option>
                {groups.map((g) => (
                  <option key={g.crop} value={g.crop}>{t('cropOption', { crop: cropName(g.crop), n: g.items.length })}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {products === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : shown.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Tags className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('emptyTitle')}</p>
            <p className="text-sm mt-1">
              {canAdmin
                ? t('emptyAdmin')
                : t('emptySeat')}
            </p>
            {canAdmin && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/nursery/products/new')}>
                <Plus className="h-4 w-4" /> {t('newProduct')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {shownGroups.map((group) => (
              <section key={group.crop}>
                {hasCrops && (
                  <h2 className="flex items-baseline gap-2 mb-3 text-base font-semibold text-neutral-800">
                    <span className="min-w-0 break-words">{cropName(group.crop)}</span>
                    <span className="text-xs font-normal text-neutral-500">{group.items.length}</span>
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map((p) => (
                    <ProductCard key={p.id} product={p} crop={hasCrops ? cropOf(p) : null} editable={canAdmin} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default ProductList;
